/**
 * Normalizes `GetShipmentBookingById` payloads the same way as
 * `pollForConfirmationUrl` in BookShipmentDrawer.
 *
 * Backend uses `CustomResponse` + `return_class`: axios `data.data` is meta,
 * booking fields live at `data.data.data`. Some responses/tests may omit one
 * level or use an extra nested `data`; we unwrap defensively and accept
 * camelCase `quickstatPortalOrderNumber`.
 */

function pickPortalRaw(row: Record<string, unknown> | null): unknown {
  if (!row) return null;
  const camel = (row as { quickstatPortalOrderNumber?: unknown })
    .quickstatPortalOrderNumber;
  return row.quickstat_portal_order_number ?? camel;
}

function pickBookingStatusRaw(row: Record<string, unknown> | null): unknown {
  if (!row) return null;
  const camel = (row as { bookingStatus?: unknown }).bookingStatus;
  return row.booking_status ?? camel;
}

const MAX_DEEP_SCAN_DEPTH = 14;

function deepFindPortal(node: unknown, depth = 0): string | null {
  if (depth > MAX_DEEP_SCAN_DEPTH || node == null) return null;
  if (typeof node !== 'object') return null;
  if (Array.isArray(node)) {
    for (const el of node) {
      const found = deepFindPortal(el, depth + 1);
      if (found) return found;
    }
    return null;
  }
  const o = node as Record<string, unknown>;
  const direct = normalizePortal(pickPortalRaw(o));
  if (direct) return direct;
  for (const v of Object.values(o)) {
    const found = deepFindPortal(v, depth + 1);
    if (found) return found;
  }
  return null;
}

function deepFindBookingStatus(node: unknown, depth = 0): string | null {
  if (depth > MAX_DEEP_SCAN_DEPTH || node == null) return null;
  if (typeof node !== 'object') return null;
  if (Array.isArray(node)) {
    for (const el of node) {
      const found = deepFindBookingStatus(el, depth + 1);
      if (found) return found;
    }
    return null;
  }
  const o = node as Record<string, unknown>;
  const direct = normalizeStatus(pickBookingStatusRaw(o));
  if (direct) return direct;
  for (const v of Object.values(o)) {
    const found = deepFindBookingStatus(v, depth + 1);
    if (found) return found;
  }
  return null;
}

/** Prefer the dict that looks most like a ShipmentBooking serializer payload. */
function deepFindBookingRow(root: unknown): Record<string, unknown> | null {
  let best: Record<string, unknown> | null = null;
  let bestScore = 0;

  function score(o: Record<string, unknown>): number {
    let s = 0;
    if (typeof o.pickup_person === 'string' && o.pickup_person.trim()) s += 2;
    if (typeof o.delivery_person === 'string' && o.delivery_person.trim())
      s += 2;
    if (typeof o.pickup_address === 'string' && o.pickup_address.trim()) s += 1;
    if (typeof o.delivery_address === 'string' && o.delivery_address.trim())
      s += 1;
    if (pickPortalRaw(o)) s += 1;
    return s;
  }

  function walk(node: unknown, depth: number): void {
    if (depth > MAX_DEEP_SCAN_DEPTH || node == null) return;
    if (typeof node !== 'object') return;
    if (Array.isArray(node)) {
      for (const el of node) walk(el, depth + 1);
      return;
    }
    const o = node as Record<string, unknown>;
    const sc = score(o);
    if (sc > bestScore) {
      bestScore = sc;
      best = o;
    }
    for (const v of Object.values(o)) walk(v, depth + 1);
  }

  walk(root, 0);
  return bestScore >= 4 ? best : null;
}

function rowLooksUsable(row: Record<string, unknown> | null): boolean {
  if (!row) return false;
  return (
    pickPortalRaw(row) != null ||
    typeof row.pickup_person === 'string' ||
    typeof row.delivery_person === 'string'
  );
}

function normalizePortal(raw: unknown): string | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  return s !== '' ? s : null;
}

function normalizeStatus(raw: unknown): string | null {
  if (raw == null) return null;
  const s = String(raw).trim().toLowerCase();
  return s !== '' ? s : null;
}

/**
 * Resolve the booking dict from meta (`wrap`) returned at `axios.data.data`.
 * Handles: meta.data = booking, or meta.data.data = booking (double nest).
 */
function bookingPayloadFromWrap(
  wrap: Record<string, unknown> | null
): Record<string, unknown> | null {
  if (!wrap) return null;

  const first = wrap.data;
  if (!first || typeof first !== 'object' || Array.isArray(first)) {
    if (
      'quickstat_portal_order_number' in wrap ||
      'booking_status' in wrap ||
      'pickup_person' in wrap
    ) {
      return wrap;
    }
    return null;
  }

  const firstObj = first as Record<string, unknown>;
  const second = firstObj.data;
  if (
    second &&
    typeof second === 'object' &&
    !Array.isArray(second) &&
    (pickPortalRaw(second as Record<string, unknown>) != null ||
      'booking_status' in (second as object) ||
      'pickup_person' in (second as object))
  ) {
    return second as Record<string, unknown>;
  }

  return firstObj;
}

export function parseShipmentBookingFromGetResponse(res: unknown): {
  row: Record<string, unknown> | null;
  portalOrder: string | null;
  /** Lowercase `ShipmentBooking.booking_status` when present. */
  bookingStatus: string | null;
} {
  const ax = res as { data?: { data?: unknown } };
  const wrap = ax?.data?.data as Record<string, unknown> | undefined;

  let booking = wrap != null ? bookingPayloadFromWrap(wrap) : null;

  if (!rowLooksUsable(booking)) {
    booking = deepFindBookingRow(res);
  }

  const portalOrder =
    normalizePortal(pickPortalRaw(booking)) ?? deepFindPortal(res);
  const bookingStatus =
    normalizeStatus(pickBookingStatusRaw(booking)) ??
    deepFindBookingStatus(res);

  return {
    row: booking,
    portalOrder,
    bookingStatus,
  };
}
